/**
 * Central status -> semantic tone mapping so every status badge across the app
 * (animal status, payment status, treatment outcome, approval status, meter
 * status, notification priority...) uses the same green/amber/red/blue language.
 * Per UI spec #7: color is never the only signal — ILMSStatusBadge always pairs
 * tone with an icon and the text label.
 */

export type Tone = 'success' | 'warning' | 'critical' | 'info' | 'neutral';

const TONE_MAP: Record<string, Tone> = {
  // Animal health_status (v2)
  Active: 'success',
  Sold: 'info',
  Dead: 'critical',
  Deceased: 'critical', // legacy v1 value, kept for safety
  Disposed: 'neutral',
  Returned: 'warning',
  Quarantine: 'warning',
  Hospitalized: 'warning',
  Transferred: 'info',
  // Animal production_status (v2)
  Dry: 'neutral',
  Pregnant: 'info',
  Lactating: 'success',
  PregnantLactating: 'success',
  PregnantDry: 'info',
  Sick: 'critical',
  // Payment / approval status
  Paid: 'success',
  Partial: 'warning',
  Unpaid: 'critical',
  Approved: 'success',
  Pending: 'warning',
  Rejected: 'critical',
  // Users
  Inactive: 'neutral',
  Locked: 'critical',
  Disabled: 'critical',
  // Meter status
  Replaced: 'warning',
  UnderMaintenance: 'warning',
  // Breeding / lifecycle outcomes
  Calved: 'success',
  Failed: 'critical',
  Aborted: 'critical',
  Conceived: 'success',
  NotConceived: 'warning',
  Positive: 'success',
  Negative: 'neutral',
  Live: 'success',
  Stillborn: 'critical',
  Mixed: 'warning',
  // Purchase order / procurement status
  Draft: 'neutral',
  PartiallyReceived: 'warning',
  Received: 'success',
  Cancelled: 'critical',
  // Treatment / task / outbreak status
  Ongoing: 'info',
  Completed: 'success',
  Discontinued: 'neutral',
  Verified: 'success',
  InProgress: 'info',
  Contained: 'warning',
  Closed: 'neutral',
  DriedOff: 'neutral',
  Expired: 'critical',
  Revoked: 'critical',
  // Public enquiries
  New: 'info',
  Contacted: 'warning',
  // Notifications
  Unread: 'info',
  Read: 'neutral',
  Low: 'neutral',
  Medium: 'info',
  High: 'warning',
  Critical: 'critical',
  // Activity result
  SUCCESS: 'success',
  FAILURE: 'critical',
};

export function toneForStatus(status: string | undefined | null): Tone {
  if (!status) return 'neutral';
  return TONE_MAP[status] ?? 'neutral';
}

/** "PregnantLactating" -> "Pregnant Lactating", "UnderMaintenance" -> "Under
 * Maintenance". Enum values are stored/keyed PascalCase with no separator,
 * but a badge showing two words jammed together reads as a typo — this is
 * display-only, so it never touches the raw value used for TONE_MAP lookups
 * or API payloads. Single-word statuses (the vast majority) pass through
 * unchanged since there's no lowercase-to-uppercase transition to split on.
 * SUCCESS/FAILURE (all-caps) are also unaffected. */
export function humanizeStatus(status: string | undefined | null): string {
  if (!status) return '';
  return status.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
}

export const TONE_CLASSES: Record<Tone, { bg: string; text: string; border: string; dot: string }> = {
  success: { bg: 'bg-success-50', text: 'text-success-700', border: 'border-success-100', dot: 'bg-success-500' },
  warning: { bg: 'bg-warning-50', text: 'text-warning-700', border: 'border-warning-100', dot: 'bg-warning-500' },
  critical: { bg: 'bg-critical-50', text: 'text-critical-700', border: 'border-critical-100', dot: 'bg-critical-500' },
  info: { bg: 'bg-info-50', text: 'text-info-700', border: 'border-info-100', dot: 'bg-info-500' },
  neutral: { bg: 'bg-surface-muted', text: 'text-ink-500', border: 'border-ink-300/30', dot: 'bg-ink-400' },
};
